import React, { useEffect, useState, useRef } from "react";
import {
	alpha,
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	Paper,
	Typography,
	useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import QrCodeScannerTwoToneIcon from "@mui/icons-material/QrCodeScannerTwoTone";
import CameraAltTwoToneIcon from "@mui/icons-material/CameraAltTwoTone";
import WarningAmberTwoToneIcon from "@mui/icons-material/WarningAmberTwoTone";
import LoginTwoToneIcon from "@mui/icons-material/LoginTwoTone";
import LogoutTwoToneIcon from "@mui/icons-material/LogoutTwoTone";

interface Props {
	open: boolean;
	onClose: () => void;
	onScan: (decodedText: string) => void;
	scanType?: "IN" | "OUT";
	onScanTypeChange?: (type: "IN" | "OUT") => void;
}

const loadHtml5QrcodeScript = (): Promise<any> => {
	return new Promise((resolve, reject) => {
		if ((window as any).Html5QrcodeScanner) {
			resolve((window as any).Html5QrcodeScanner);
			return;
		}

		// Check if script element already exists
		const existingScript = document.getElementById("html5-qrcode-cdn-script");
		if (existingScript) {
			existingScript.addEventListener("load", () => {
				resolve((window as any).Html5QrcodeScanner);
			});
			existingScript.addEventListener("error", (e) => reject(e));
			return;
		}

		const script = document.createElement("script");
		script.id = "html5-qrcode-cdn-script";
		script.src = "https://unpkg.com/html5-qrcode@2.3.8/html5-qrcode.min.js";
		script.async = true;
		script.onload = () => {
			resolve((window as any).Html5QrcodeScanner);
		};
		script.onerror = (error) => {
			console.error("Failed to load html5-qrcode script from CDN", error);
			reject(error);
		};
		document.body.appendChild(script);
	});
};

const QuickQRScannerModal: React.FC<Props> = ({
	open,
	onClose,
	onScan,
	scanType = "IN",
	onScanTypeChange,
}) => {
	const theme = useTheme();
	const [isLoadingScript, setIsLoadingScript] = useState<boolean>(true);
	const [scriptError, setScriptError] = useState<string | null>(null);
	const scannerRef = useRef<any>(null);

	const isSecure =
		window.isSecureContext ||
		window.location.hostname === "localhost" ||
		window.location.hostname === "127.0.0.1";

	useEffect(() => {
		if (!open) return;

		let isMounted = true;

		if (!isSecure) {
			setIsLoadingScript(false);
			return;
		}

		setIsLoadingScript(true);
		setScriptError(null);

		loadHtml5QrcodeScript()
			.then((Html5QrcodeScannerClass) => {
				if (!isMounted) return;
				setIsLoadingScript(false);

				// Wait a tick for the DOM element #reader-modal to be available
				setTimeout(() => {
					if (!isMounted) return;
					const readerElement = document.getElementById("reader-modal");
					if (!readerElement) return;

					try {
						// Clean up old instance if any
						if (scannerRef.current) {
							scannerRef.current.clear().catch(() => { });
						}

						const scanner = new Html5QrcodeScannerClass(
							"reader-modal",
							{
								fps: 10,
								qrbox: { width: 240, height: 240 },
								rememberLastUsedCamera: true,
								supportedScanTypes: [0], // SCAN_TYPE_CAMERA
							},
							/* verbose= */ false
						);

						scannerRef.current = scanner;

						scanner.render(
							(decodedText: string) => {
								// Success callback
								try {
									scanner.clear().catch(() => { });
								} catch (e) { }
								onScan(decodedText);
							},
							(_errorMessage: string) => {
								// Noise, ignoring
							}
						);
					} catch (err: any) {
						console.error("Error initializing scanner:", err);
						setScriptError(err?.message || "Failed to start camera scanner.");
					}
				}, 150);
			})
			.catch((err) => {
				if (!isMounted) return;
				setIsLoadingScript(false);
				setScriptError("Failed to load camera library. Please check your internet connection.");
			});

		return () => {
			isMounted = false;
			if (scannerRef.current) {
				try {
					scannerRef.current.clear().catch(() => { });
				} catch (e) { }
				scannerRef.current = null;
			}
		};
	}, [open, isSecure, onScan]);

	const handleDialogClose = () => {
		if (scannerRef.current) {
			try {
				scannerRef.current.clear().catch(() => { });
			} catch (e) { }
			scannerRef.current = null;
		}
		onClose();
	};

	return (
		<Dialog
			open={open}
			onClose={handleDialogClose}
			maxWidth="sm"
			fullWidth
			disableEnforceFocus
		>
			<DialogTitle
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					pb: 1,
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
					<CameraAltTwoToneIcon color="primary" />
					<Box>
						<Typography variant="h4" fontWeight={700}>
							Quick Camera Scan
						</Typography>
						<Typography variant="h6" color="text.secondary">
							Scan ticket QR code using your device camera
						</Typography>
					</Box>
				</Box>
				<IconButton onClick={handleDialogClose} size="small">
					<CloseIcon />
				</IconButton>
			</DialogTitle>

			<Divider />

			<DialogContent sx={{ pt: 2, pb: 2, textAlign: "center" }}>
				{/* Scan Mode Toggle in Header */}
				{onScanTypeChange && (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							gap: 1,
							mb: 2,
						}}
					>
						<Typography variant="caption" fontWeight={700} color="text.secondary">
							SCAN AS:
						</Typography>
						<Chip
							icon={<LoginTwoToneIcon sx={{ fontSize: "15px !important" }} />}
							label="IN (Entrance)"
							color={scanType === "IN" ? "primary" : "default"}
							variant={scanType === "IN" ? "filled" : "outlined"}
							size="small"
							onClick={() => onScanTypeChange("IN")}
							clickable
							sx={{ fontWeight: 700 }}
						/>
						<Chip
							icon={<LogoutTwoToneIcon sx={{ fontSize: "15px !important" }} />}
							label="OUT (Exit)"
							color={scanType === "OUT" ? "primary" : "default"}
							variant={scanType === "OUT" ? "filled" : "outlined"}
							size="small"
							onClick={() => onScanTypeChange("OUT")}
							clickable
							sx={{ fontWeight: 700 }}
						/>
					</Box>
				)}

				{!isSecure ? (
					<Alert
						severity="error"
						icon={<WarningAmberTwoToneIcon />}
						sx={{
							textAlign: "left",
							borderRadius: 2,
							mb: 2,
						}}
					>
						<Typography variant="subtitle2" fontWeight={700} gutterBottom>
							Security Restriction
						</Typography>
						Camera access is only allowed in <b>Secure Contexts</b> (HTTPS or Localhost). If you are testing on mobile, please connect via an <b>HTTPS</b> connection.
					</Alert>
				) : scriptError ? (
					<Alert severity="error" sx={{ textAlign: "left", borderRadius: 2, mb: 2 }}>
						{scriptError}
					</Alert>
				) : isLoadingScript ? (
					<Box sx={{ py: 6, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
						<CircularProgress size={36} />
						<Typography variant="body2" color="text.secondary">
							Initializing camera scanner...
						</Typography>
					</Box>
				) : (
					<Box>
						<Box
							id="reader-modal"
							sx={{
								width: "100%",
								maxWidth: "460px",
								mx: "auto",
								borderRadius: 2,
								overflow: "hidden",
								"& video": {
									borderRadius: "8px",
									width: "100% !important",
								},
								"& #reader-modal__scan_region": {
									background: "rgba(0, 0, 0, 0.05)",
									borderRadius: "8px",
								},
								"& #reader-modal__dashboard_section_csr button": {
									bgcolor: theme.palette.primary.main,
									color: "#ffffff",
									border: "none",
									padding: "6px 14px",
									borderRadius: "6px",
									fontWeight: 600,
									cursor: "pointer",
									margin: "4px",
									fontSize: "0.85rem",
									"&:hover": {
										bgcolor: theme.palette.primary.dark,
									},
								},
								"& #reader-modal__dashboard_section_csr select": {
									padding: "6px 10px",
									borderRadius: "6px",
									border: "1px solid #ccc",
									fontSize: "0.85rem",
									margin: "4px",
								},
							}}
						/>

						<Paper
							variant="outlined"
							sx={{
								p: 1.2,
								mt: 2,
								borderRadius: 1.5,
								bgcolor: alpha(theme.palette.primary.main, 0.04),
								borderColor: alpha(theme.palette.primary.main, 0.15),
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								gap: 1,
							}}
						>
							<QrCodeScannerTwoToneIcon color="primary" fontSize="small" />
							<Typography variant="h6" color="text.secondary" fontWeight={600}>
								Align the QR code within the camera frame to scan automatically.
							</Typography>
						</Paper>
					</Box>
				)}
			</DialogContent>

			<Divider />

			<DialogActions sx={{ px: 2.5, py: 1.5, justifyContent: "space-between" }}>
				<Typography variant="h6" color="text.secondary">
					Tip: Ensure good lighting for fast detection.
				</Typography>
				<Button
					onClick={handleDialogClose}
					size="small"
					variant="outlined"
					color="inherit"
					startIcon={<CloseIcon fontSize="small" />}
				>
					Close Scanner
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default QuickQRScannerModal;
